if (!customElements.get('sticky-atc-panel')) {
  // ===== DEBUG LOGGING =====
  const LOG_PREFIX = '[StickyATC]';
  const log = (...args) => console.log(LOG_PREFIX, ...args);
  const logGroup = (label) => console.group(LOG_PREFIX + ' ' + label);
  const logGroupEnd = () => console.groupEnd();

  let scrollLogCount = 0;
  const SCROLL_LOG_LIMIT = 30; // throttle scroll logs

  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();
      logGroup('constructor');
      log('this element:', this);
      log('classes:', this.className);

      this.productSection = this.closest('.cc-main-product');
      log('productSection found:', !!this.productSection, this.productSection);

      if (!this.productSection) {
        log('❌ NO productSection — abort');
        logGroupEnd();
        return;
      }

      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      log('productInfo found:', !!this.productInfo);
      log('productForm found:', !!this.productForm);

      if (this.productForm) {
        const r = this.productForm.getBoundingClientRect();
        log('productForm initial rect:', { top: r.top, bottom: r.bottom, height: r.height });
      }

      log('IntersectionObserver supported:', 'IntersectionObserver' in window);
      log('MutationObserver supported:', 'MutationObserver' in window);

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      } else {
        log('❌ Observers not supported — abort');
      }
      logGroupEnd();
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      log('disconnectedCallback fired');
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      const isTopDesktop = this.classList.contains('sticky-atc-panel--top-desktop');
      log('bindEvents: isTopDesktop =', isTopDesktop);

      // Observer per mobile (sempre) e desktop bottom (default)
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const targetName = entry.target === this.productForm ? 'productForm' : entry.target === this.productInfo ? 'productInfo' : 'unknown';

          // Mobile: trigger basato su productForm bottom
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            const newOut = entry.boundingClientRect.bottom > 0;
            log('IO mobile fire:', { target: targetName, bottom: entry.boundingClientRect.bottom, willBeOut: newOut });
            this.classList.toggle('sticky-atc-panel--out', newOut);
            return;
          }

          // Desktop BOTTOM (default): trigger su productInfo intersecting.
          if (entry.target === this.productInfo && theme.mediaMatches.md && !isTopDesktop) {
            log('IO desktop-bottom fire:', { target: targetName, isIntersecting: entry.isIntersecting, willBeOut: entry.isIntersecting });
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      // Per desktop con posizione TOP: scroll-based trigger
      if (isTopDesktop) {
        log('TopDesktop branch activated, theme.mediaMatches.md =', window.theme?.mediaMatches?.md);
        this.aacTriggerY = null;

        this.computeAacTriggerY = () => {
          if (!this.productForm) {
            log('❌ computeAacTriggerY: no productForm');
            return;
          }
          const rect = this.productForm.getBoundingClientRect();
          this.aacTriggerY = window.scrollY + rect.top + rect.height * 0.5;
          log('✅ computeAacTriggerY:', {
            scrollY: window.scrollY,
            rectTop: rect.top,
            rectHeight: rect.height,
            aacTriggerY: this.aacTriggerY,
          });
        };

        // Top position del panel:
        //  - Default: top:0 (cover any sticky header underneath). z-index alto
        //    (999 da CSS) per stare sopra eventuali header sticky.
        //  - PDP-sticky-stack mode (body.pdp-sticky-stack): l'header resta visibile
        //    e la sticky ATC scivola SOTTO l'header, formando un blocco unico.
        //    Il top reale è il bottom dell'header sticky corrente.
        const getHeaderBottomPx = () => {
          if (!document.body.classList.contains('pdp-sticky-stack')) return 0;
          const header = document.querySelector('.cc-header .header') || document.querySelector('.cc-header');
          if (!header) return 0;
          const bottom = header.getBoundingClientRect().bottom;
          return bottom > 0 ? bottom : 0;
        };

        this.handleTopDesktopScroll = () => {
          const mdMatches = theme?.mediaMatches?.md;
          if (!mdMatches) {
            if (scrollLogCount < SCROLL_LOG_LIMIT) {
              log('scroll: skipped, not desktop');
              scrollLogCount++;
            }
            return;
          }
          if (this.aacTriggerY === null) {
            if (scrollLogCount < SCROLL_LOG_LIMIT) {
              log('scroll: skipped, aacTriggerY not yet computed');
              scrollLogCount++;
            }
            return;
          }
          const shouldShow = window.scrollY > this.aacTriggerY;
          const headerBottom = getHeaderBottomPx();

          if (scrollLogCount < SCROLL_LOG_LIMIT) {
            log('scroll handler:', {
              scrollY: window.scrollY,
              aacTriggerY: this.aacTriggerY,
              shouldShow,
              currentHasOut: this.classList.contains('sticky-atc-panel--out'),
              headerBottom,
            });
            scrollLogCount++;
          }

          this.classList.toggle('sticky-atc-panel--out', !shouldShow);

          // Top-desktop e' full-width: transform sempre 'none' (no translateX(-50%)
          // del --center variant). La centratura del contenuto avviene via CSS
          // sul container .card--sticky-atc interno.
          if (shouldShow) {
            this.classList.remove('invisible');
            this.style.cssText = `
              visibility: visible !important;
              opacity: 1 !important;
              top: ${headerBottom}px !important;
              bottom: auto !important;
              left: 0 !important;
              right: 0 !important;
              transform: none !important;
            `;
            if (scrollLogCount <= 3 || scrollLogCount % 10 === 0) {
              const cs = getComputedStyle(this);
              log('AFTER show — computed:', {
                visibility: cs.visibility,
                opacity: cs.opacity,
                top: cs.top,
                bottom: cs.bottom,
                transform: cs.transform.slice(0, 50),
                rect: this.getBoundingClientRect(),
                headerBottom,
              });
            }
          } else {
            this.style.cssText = `
              visibility: hidden !important;
              opacity: 0 !important;
              top: ${headerBottom}px !important;
              bottom: auto !important;
              left: 0 !important;
              right: 0 !important;
              transform: translateY(-100%) !important;
            `;
          }
        };

        // Compute trigger Y dopo che il layout e' stabile (next paint)
        if ('requestAnimationFrame' in window) {
          requestAnimationFrame(() => {
            requestAnimationFrame(this.computeAacTriggerY);
          });
        } else {
          setTimeout(this.computeAacTriggerY, 100);
        }

        // Ricomputa al resize (layout puo' shiftare i blocchi)
        window.addEventListener('on:debounced-resize', this.computeAacTriggerY);

        window.addEventListener('scroll', this.handleTopDesktopScroll, { passive: true });
        log('✅ scroll listener attached for top desktop');
      }

      // Wire up del CTA "AGGIUNGI AL CARRELLO" del sticky panel.
      // Quando l'utente clicca:
      //   - Se il bottone ATC originale del product-form e' attivo (variante
      //     selezionata e disponibile), simula il click su quello -> ATC
      //     diretto via product-form custom element
      //   - Se disabled (variante non scelta o sold out), lascia il default
      //     link behavior che scrolla al variant picker
      const stickyAtcBtn = this.querySelector('.sticky-atc-panel__atc-btn');
      const originalAtcBtn = this.productSection
        ? this.productSection.querySelector('product-form button[type="submit"][name="add"]')
        : null;
      log('Sticky ATC wire-up:', {
        stickyAtcBtn: !!stickyAtcBtn,
        originalAtcBtn: !!originalAtcBtn,
        originalDisabled: originalAtcBtn ? originalAtcBtn.disabled : null,
      });

      if (stickyAtcBtn && originalAtcBtn) {
        stickyAtcBtn.addEventListener('click', (e) => {
          // Re-read disabled state al click (variante puo' cambiare)
          if (originalAtcBtn.disabled) {
            log('sticky ATC click: original disabled, fallback to scroll anchor');
            return; // lascia default (scroll al variant picker via href)
          }
          log('sticky ATC click: triggering original ATC submit');
          e.preventDefault();
          originalAtcBtn.click();
        });

        /* IB-600 (Matteo 18/06): la CTA sticky deve comportarsi in modo
           identico a quella non-sticky. Cliccando, simuliamo gia' il click
           sull'ATC originale (sopra), quindi parte lo stesso identico flusso
           (aggiunta + apertura cart drawer). Nessun feedback/copy dedicato sul
           bottone sticky: il cambio di testo temporaneo precedente confondeva
           l'utente e "spezzava" il collegamento percepito col carrello. */
      }

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
  log('✅ customElements.define called');
}
